// เหตุการณ์เฉพาะตัวของสัตว์เลี้ยงลูกด้วยนมที่มีชื่อเสียง (ดู signature.ts)

import type { GameEvent } from "../../engine/types";
import { sig } from "./signature";

const ADULT = ["adult", "old"] as const;

export const SIGNATURE_MAMMAL_EVENTS: readonly GameEvent[] = [
  // ---------- สัตว์กินเนื้อ ----------
  sig("Panthera tigris", "tiger_territory", {
    stageTags: ADULT,
    text: "คุณข่วนเปลือกไม้และพ่นปัสสาวะทิ้งกลิ่นไว้ตามเส้นทาง ประกาศว่าป่าผืนนี้เป็นของคุณ",
    fact: "ลายของเสือโคร่งแต่ละตัวไม่ซ้ำกัน นักวิจัยจึงนับจำนวนเสือในป่าได้จากภาพของกล้องดักถ่าย",
    choices: [
      {
        label: "ขยายอาณาเขตให้กว้างขึ้น",
        outcomes: [
          { chance: 0.6, text: "ไม่มีเสือตัวไหนกล้าท้าทาย อาณาเขตใหม่มีเหยื่อชุกชุม", effects: { fullness: 20, safety: 10, move: "near" } },
          { chance: 0.4, text: "เสือตัวผู้อีกตัวไม่ยอม คุณต้องสู้จนได้แผล", effects: { health: -25, addFlags: ["injured"], cause: "accident" } },
        ],
      },
      { label: "รักษาอาณาเขตเดิมไว้", outcomes: [{ chance: 1, text: "คุณเดินตรวจเขตแดนจนทั่ว ทุกอย่างยังเป็นของคุณ", effects: { safety: 10 } }] },
    ],
  }),
  sig("Panthera pardus", "leopard_tree", {
    stageTags: ADULT,
    text: "คุณล่าเก้งได้ตัวหนึ่ง แล้วได้ยินเสียงหวีดของฝูงหมาในกำลังเข้ามาใกล้",
    fact: "เสือดาวลากเหยื่อที่หนักพอๆ กับตัวเองขึ้นไปเก็บไว้บนต้นไม้ เพื่อไม่ให้สัตว์อื่นแย่ง",
    choices: [
      { label: "ลากเหยื่อขึ้นต้นไม้", outcomes: [{ chance: 1, text: "คุณลากเหยื่อขึ้นไปพาดกิ่งสูง กินได้อีกหลายวันโดยไม่มีใครแย่ง", effects: { fullness: 40 } }] },
      {
        label: "กินให้เร็วที่สุดตรงนี้",
        outcomes: [
          { chance: 0.5, text: "คุณกินได้มากพอก่อนฝูงหมาในจะมาถึง", effects: { fullness: 25 } },
          { chance: 0.5, text: "ฝูงหมาในมาถึงและแย่งเหยื่อไป", effects: { fullness: 5, safety: -10 } },
        ],
      },
    ],
  }),
  sig("Neofelis nebulosa", "clouded_leopard_canopy", {
    text: "คุณไต่ลงจากต้นไม้สูงแบบเอาหัวลง ตาจับจ้องลิงที่กำลังหลับอยู่กิ่งข้างล่าง",
    fact: "เสือลายเมฆมีเขี้ยวยาวเมื่อเทียบกับขนาดตัวมากที่สุดในบรรดาแมวที่ยังมีชีวิตอยู่ และหมุนข้อเท้าหลังได้จนไต่ต้นไม้เอาหัวลงได้",
    choices: [
      {
        label: "โจนลงไปจากกิ่งไม้",
        outcomes: [
          { chance: 0.6, text: "คุณตะครุบเหยื่อได้อย่างแม่นยำ", effects: { fullness: 35 } },
          { chance: 0.4, text: "กิ่งไม้หัก คุณร่วงลงพื้นแต่ตั้งหลักทันด้วยหางยาว", effects: { health: -10, fullness: -5 } },
        ],
      },
      { label: "รอจังหวะที่ดีกว่านี้", outcomes: [{ chance: 1, text: "ลิงตื่นและหนีไป แต่คุณก็ไม่เสียแรงเปล่า", effects: { fullness: -5, safety: 5 } }] },
    ],
  }),
  sig("Cuon alpinus", "dhole_whistle", {
    text: "สมาชิกในฝูงส่งเสียงหวีดแหลมเรียกกันกลางป่า กวางป่าตัวหนึ่งเพิ่งเดินผ่านไป",
    fact: "หมาในล่าเป็นฝูงและส่งเสียงหวีดคล้ายนกหวีดหากันในป่าทึบ จึงล่าเหยื่อตัวใหญ่กว่าตัวเองได้หลายเท่า",
    choices: [
      {
        label: "ร่วมไล่ต้อนกับฝูง",
        outcomes: [
          { chance: 0.7, text: "ฝูงช่วยกันต้อนกวางจนหมดแรง ทุกตัวได้กินอิ่ม", effects: { fullness: 35 } },
          { chance: 0.3, text: "กวางหันมาขวิด คุณถูกเขากระแทกเข้าเต็มแรง", effects: { health: -25, cause: "accident" } },
        ],
      },
      { label: "เฝ้าลูกๆ ที่โพรงแทน", outcomes: [{ chance: 1, text: "ฝูงนำเนื้อกลับมาสำรอกให้คุณและลูกๆ กิน", effects: { fullness: 20, safety: 10 } }] },
    ],
  }),
  sig("Helarctos malayanus", "sun_bear_honey", {
    text: "คุณได้กลิ่นน้ำผึ้งจากโพรงไม้สูงบนต้นยาง ผึ้งบินวนอยู่รอบปากโพรง",
    fact: "หมีหมาเป็นหมีที่ตัวเล็กที่สุดในโลก และมีลิ้นยาวราว 20–25 ซม. ไว้เลียน้ำผึ้งและแมลงจากโพรงไม้",
    choices: [
      {
        label: "ปีนขึ้นไปฉีกโพรงไม้",
        outcomes: [
          { chance: 0.7, text: "คุณใช้เล็บยาวฉีกเนื้อไม้ แล้วยื่นลิ้นยาวเข้าไปเลียน้ำผึ้งจนหมดรัง", effects: { fullness: 40 } },
          { chance: 0.3, text: "ผึ้งรุมต่อยจนคุณต้องรีบลงมา", effects: { fullness: 10, health: -15 } },
        ],
      },
      { label: "แกะขอนไม้ผุหาปลวกแทน", outcomes: [{ chance: 1, text: "ปลวกเต็มขอนไม้ ไม่ต้องเสี่ยงโดนผึ้งต่อย", effects: { fullness: 20 } }] },
    ],
  }),
  sig("Lutrogale perspicillata", "otter_family_hunt", {
    text: "ครอบครัวนากของคุณเจอฝูงปลาในลำธาร ทุกตัวหันมามองหน้ากัน",
    fact: "นากใหญ่ขนเรียบล่าปลาเป็นครอบครัว ว่ายเป็นแถวช่วยกันต้อนฝูงปลาเข้าหาฝั่งหรือที่ตื้น",
    choices: [
      { label: "ช่วยกันต้อนปลาเข้าที่ตื้น", outcomes: [{ chance: 1, text: "ปลาติดอยู่ในแอ่งตื้น ทั้งครอบครัวได้กินกันอิ่ม", effects: { fullness: 35, safety: 5 } }] },
      {
        label: "แยกไปจับเองตัวเดียว",
        outcomes: [
          { chance: 0.5, text: "คุณจับปลาตัวใหญ่ได้คนเดียว", effects: { fullness: 25 } },
          { chance: 0.5, text: "ปลาหนีไปหมด คุณว่ายกลับมาหาครอบครัวมือเปล่า", effects: { fullness: -5 } },
        ],
      },
    ],
  }),
  sig("Panthera leo", "lion_pride", {
    stageTags: ADULT,
    text: "สิงโตตัวผู้แปลกหน้าสองตัวเดินเข้ามาใกล้ฝูงของคุณ",
    fact: "สิงโตเป็นแมวชนิดเดียวที่อยู่เป็นฝูง ตัวเมียในฝูงมักเป็นญาติกัน ช่วยกันล่าและเลี้ยงลูกร่วมกัน",
    choices: [
      {
        label: "รวมฝูงออกไปขับไล่",
        outcomes: [
          { chance: 0.65, text: "ทั้งฝูงคำรามพร้อมกัน สิงโตแปลกหน้าถอยไป", effects: { safety: 10 } },
          { chance: 0.35, text: "การต่อสู้ดุเดือด คุณได้แผลลึก", effects: { health: -30, addFlags: ["injured"], cause: "accident" } },
        ],
      },
      { label: "พาลูกๆ หลบไปที่อื่น", outcomes: [{ chance: 1, text: "คุณพาลูกหลบไปซ่อนในพุ่มไม้จนเรื่องสงบ", effects: { fullness: -10, move: "near" } }] },
    ],
  }),
  sig("Acinonyx jubatus", "cheetah_sprint", {
    stageTags: ADULT,
    text: "อิมพาลาตัวหนึ่งเล็มหญ้าอยู่ห่างออกไปไม่ไกล ลมพัดมาทางคุณพอดี",
    fact: "เสือชีตาห์เป็นสัตว์บกที่วิ่งเร็วที่สุด แต่วิ่งเต็มความเร็วได้แค่ช่วงสั้นๆ ไม่กี่ร้อยเมตร หลังล่าแล้วต้องพักหอบนานจนมักถูกสัตว์อื่นแย่งเหยื่อ",
    choices: [
      {
        label: "ย่องเข้าไปใกล้ที่สุดก่อนพุ่ง",
        outcomes: [
          { chance: 0.6, text: "คุณพุ่งตัวด้วยความเร็วสูงสุดและล้มเหยื่อได้", effects: { fullness: 35 } },
          { chance: 0.4, text: "คุณจับได้ แต่ระหว่างนั่งหอบ ไฮยีนาก็มาแย่งไป", effects: { fullness: 5, safety: -10 } },
        ],
      },
      { label: "วิ่งไล่ตั้งแต่ระยะไกล", outcomes: [{ chance: 1, text: "อิมพาลาหนีพ้นก่อนคุณจะหมดแรง", effects: { fullness: -15 } }] },
    ],
  }),
  sig("Crocuta crocuta", "hyena_clan", {
    text: "ฝูงสิงโตกำลังกินม้าลายอยู่ ฝูงไฮยีนาของคุณส่งเสียงหัวเราะเรียกพวกกันมารวมตัว",
    fact: "ไฮยีนาลายจุดล่าเหยื่อเองเป็นส่วนใหญ่ ไม่ได้กินแต่ซาก และในฝูงตัวเมียเป็นใหญ่กว่าตัวผู้",
    choices: [
      {
        label: "รวมพวกให้มากแล้วบุกแย่ง",
        outcomes: [
          { chance: 0.6, text: "พวกคุณมากกว่า สิงโตยอมถอยออกไป", effects: { fullness: 35 } },
          { chance: 0.4, text: "สิงโตตัวผู้ปรากฏตัว ฝูงแตกกระเจิง", effects: { health: -20, cause: "predator" } },
        ],
      },
      { label: "ออกล่าเอง", outcomes: [{ chance: 1, text: "ฝูงไล่วิลเดอบีสต์ได้หลายกิโลเมตรจนเหยื่อหมดแรง", effects: { fullness: 25 } }] },
    ],
  }),
  sig("Lycaon pictus", "wild_dog_sneeze", {
    text: "สมาชิกในฝูงตื่นจากการนอนกลางวัน เริ่มวิ่งวนทักทายกันและจามไม่หยุด",
    fact: "สุนัขป่าแอฟริกาลงคะแนนเสียงว่าจะออกล่าหรือไม่ด้วยการจาม ยิ่งในฝูงจามกันมาก ยิ่งมีโอกาสออกล่า",
    choices: [
      { label: "จามตามเพื่อนๆ", outcomes: [{ chance: 1, text: "เสียงจามดังพอ ทั้งฝูงออกล่าและได้อิมพาลามาหนึ่งตัว", effects: { fullness: 30 } }] },
      { label: "นอนต่อ", outcomes: [{ chance: 1, text: "ฝูงยังไม่ออกล่าวันนี้ ทุกตัวพักกันต่อ", effects: { fullness: -10, health: 5 } }] },
    ],
  }),
  sig("Ursus maritimus", "polar_bear_seal_hole", {
    stageTags: ADULT,
    text: "คุณได้กลิ่นแมวน้ำใต้น้ำแข็ง เจอรูหายใจเล็กๆ บนแผ่นน้ำแข็ง",
    fact: "หมีขั้วโลกมีผิวหนังสีดำใต้ขนที่โปร่งแสง และนอนรอแมวน้ำขึ้นมาหายใจที่รูน้ำแข็งได้นานหลายชั่วโมง",
    choices: [
      {
        label: "นอนนิ่งรอข้างรู",
        outcomes: [
          { chance: 0.5, text: "หลายชั่วโมงผ่านไป แมวน้ำโผล่หัวขึ้นมา คุณตะปบได้ทันที", effects: { fullness: 45 } },
          { chance: 0.5, text: "แมวน้ำไม่มา คุณเสียพลังงานไปทั้งวัน", effects: { fullness: -15 } },
        ],
      },
      { label: "ออกเดินหาที่อื่น", outcomes: [{ chance: 1, text: "คุณเดินบนน้ำแข็งไปหลายสิบกิโลเมตร", effects: { fullness: -10, move: "far" } }] },
    ],
  }),
  sig("Ursus arctos", "bear_salmon", {
    stageTags: ADULT,
    text: "ปลาแซลมอนนับพันตัวกระโดดขึ้นน้ำตก หมีตัวอื่นๆ ก็มารออยู่ด้วย",
    fact: "หมีสีน้ำตาลกินจนน้ำหนักเพิ่มขึ้นมากในฤดูใบไม้ร่วง แล้วนอนในถ้ำทั้งฤดูหนาวหลายเดือนโดยไม่กินไม่ถ่าย",
    choices: [
      {
        label: "แย่งจุดที่ดีที่สุดบนยอดน้ำตก",
        outcomes: [
          { chance: 0.6, text: "คุณได้จุดที่ดีที่สุด ปลากระโดดเข้าปากเองแทบไม่ต้องจับ", effects: { fullness: 45 } },
          { chance: 0.4, text: "หมีตัวที่ใหญ่กว่าไล่คุณออกมาพร้อมรอยกัด", effects: { health: -20, fullness: 10, cause: "accident" } },
        ],
      },
      { label: "จับปลาในน้ำตื้นท้ายน้ำ", outcomes: [{ chance: 1, text: "ปลาที่ตื้นท้ายน้ำก็มีพอ แม้จะต้องวิ่งไล่มากกว่า", effects: { fullness: 25 } }] },
    ],
  }),
  sig("Canis lupus", "wolf_howl", {
    text: "คุณพลัดหลงจากฝูงตอนตามรอยกวาง พระจันทร์ขึ้นแล้ว",
    fact: "หมาป่าหอนเพื่อบอกตำแหน่งให้สมาชิกในฝูงและเตือนฝูงอื่นไม่ให้เข้ามาใกล้ เสียงหอนได้ยินไกลหลายกิโลเมตร",
    choices: [
      {
        label: "หอนเรียกฝูง",
        outcomes: [
          { chance: 0.7, text: "เสียงหอนตอบกลับมาจากไกลๆ คุณตามเสียงกลับไปหาฝูงได้", effects: { safety: 10 } },
          { chance: 0.3, text: "ตัวที่ตอบกลับมาเป็นหมาป่าฝูงอื่น และพวกมันไม่เป็นมิตร", effects: { health: -25, cause: "accident" } },
        ],
      },
      { label: "เดินตามกลิ่นกลับเอง", outcomes: [{ chance: 1, text: "กว่าจะเจอฝูงก็เกือบรุ่งเช้า", effects: { fullness: -15 } }] },
    ],
  }),
  sig("Vulpes vulpes", "fox_mousing", {
    text: "หิมะปกคลุมทุ่ง คุณได้ยินเสียงหนูวิ่งอยู่ใต้หิมะ",
    fact: "จิ้งจอกแดงกระโดดสูงแล้วพุ่งหัวทิ่มลงหิมะเพื่อจับหนูที่ได้ยินเสียงอยู่ข้างใต้ และมักกระโดดได้แม่นกว่าเมื่อหันไปทางทิศเหนือ",
    choices: [
      {
        label: "กระโดดพุ่งหัวลงหิมะ",
        outcomes: [
          { chance: 0.6, text: "คุณโผล่ขึ้นมาพร้อมหนูในปาก", effects: { fullness: 25 } },
          { chance: 0.4, text: "หัวมุดหิมะเปล่าๆ หนูหนีไปแล้ว", effects: { fullness: -5 } },
        ],
      },
      { label: "ไปคุ้ยถังขยะใกล้หมู่บ้าน", outcomes: [{ chance: 1, text: "มีเศษอาหารให้กิน แต่สุนัขบ้านเห่าไล่", effects: { fullness: 15, safety: -10 } }] },
    ],
  }),
  // ---------- สัตว์กินพืชและอื่นๆ ----------
  sig("Elephas maximus", "elephant_rumble", {
    text: "หน้าแล้งยาวนาน ช้างแม่แปรกผู้นำโขลงส่งเสียงทุ้มต่ำที่คุณรู้สึกได้ถึงเท้า",
    fact: "ช้างสื่อสารด้วยเสียงความถี่ต่ำที่หูคนแทบไม่ได้ยิน ส่งไปได้ไกลหลายกิโลเมตร และรับรู้แรงสั่นผ่านฝ่าเท้าได้",
    choices: [
      { label: "เดินตามแม่แปรกไปหาแหล่งน้ำ", outcomes: [{ chance: 1, text: "แม่แปรกจำแหล่งน้ำเก่าได้แม่น ทั้งโขลงได้ดื่มน้ำจนพอ", effects: { fullness: 25, safety: 10, move: "near" } }] },
      {
        label: "แยกไปหากินในไร่ใกล้หมู่บ้าน",
        outcomes: [
          { chance: 0.5, text: "อ้อยในไร่หวานฉ่ำ คุณกินจนอิ่ม", effects: { fullness: 35 } },
          { chance: 0.5, text: "ชาวบ้านจุดประทัดไล่ คุณตกใจวิ่งชนรั้ว", effects: { fullness: 10, health: -15, cause: "human" } },
        ],
      },
    ],
  }),
  sig("Bos gaurus", "gaur_salt_lick", {
    text: "ฝูงกระทิงเดินมาถึงโป่งดินเค็มกลางป่า เสือโคร่งเคยเห็นแถวนี้เมื่อวาน",
    fact: "กระทิงเป็นวัวป่าที่ตัวใหญ่ที่สุดในโลก ตัวผู้สูงที่ไหล่ได้ราว 2 เมตร และชอบมากินดินโป่งเพื่อเสริมแร่ธาตุ",
    choices: [
      { label: "กินดินโป่งโดยมีตัวผู้คอยระวัง", outcomes: [{ chance: 1, text: "ทั้งฝูงได้แร่ธาตุที่ต้องการ ไม่มีอะไรกล้ามายุ่งกับฝูงกระทิง", effects: { fullness: 15, health: 10 } }] },
      { label: "เลี่ยงไปกินหญ้าที่อื่น", outcomes: [{ chance: 1, text: "ปลอดภัยดี แต่ขาดแร่ธาตุไปอีกหลายวัน", effects: { fullness: 10, health: -5 } }] },
    ],
  }),
  sig("Sus scrofa", "boar_wallow", {
    text: "อากาศร้อนจัด คุณเจอปลักโคลนกว้างกลางป่า",
    fact: "หมูป่าไม่มีต่อมเหงื่อมากพอจะระบายความร้อน จึงนอนแช่โคลนให้ตัวเย็น โคลนยังช่วยกันแมลงและปรสิตด้วย",
    choices: [
      { label: "นอนแช่ปลักโคลน", outcomes: [{ chance: 1, text: "ตัวเย็นสบาย เห็บที่เกาะอยู่หลุดออกไปพร้อมโคลน", effects: { health: 15 } }] },
      {
        label: "ขุดหาหัวมันในดินชื้นข้างปลัก",
        outcomes: [
          { chance: 0.7, text: "จมูกแข็งแรงของคุณขุดเจอหัวมันหลายหัว", effects: { fullness: 25 } },
          { chance: 0.3, text: "ร้อนจนหอบ ต้องหยุดพักกลางคัน", effects: { fullness: 5, health: -10 } },
        ],
      },
    ],
  }),
  sig("Tragulus kanchil", "mousedeer_hide", {
    text: "เสือปลาเดินผ่านมาทางคุณ ตัวคุณเล็กพอจะมุดหลบได้หลายที่",
    fact: "กระจงเล็กเป็นสัตว์กีบที่เล็กที่สุดในโลก หนักราว 2 กก. ไม่มีเขา แต่ตัวผู้มีเขี้ยวยาวโผล่ออกมานอกปาก",
    choices: [
      { label: "มุดหลบใต้รากไม้", outcomes: [{ chance: 1, text: "เสือปลาเดินผ่านไปโดยไม่เห็นคุณ", effects: { safety: 10 } }] },
      {
        label: "กระทืบเท้าเร็วๆ แล้ววิ่งหนี",
        outcomes: [
          { chance: 0.7, text: "คุณวิ่งซิกแซกหายเข้าไปในพงหนาม", effects: { fullness: -5 } },
          { chance: 0.3, text: "เสือปลาตะปบโดนสะโพก", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  }),
  sig("Muntiacus muntjak", "muntjac_bark", {
    text: "คุณเห็นเงาลายๆ เคลื่อนไหวอยู่ในพุ่มไม้",
    fact: "เก้งส่งเสียงร้องคล้ายเสียงสุนัขเห่าเมื่อตกใจหรือเห็นนักล่า บางครั้งเห่าต่อเนื่องเป็นชั่วโมง จึงได้ชื่อภาษาอังกฤษว่า barking deer",
    choices: [
      { label: "เห่าเตือนเสียงดัง", outcomes: [{ chance: 1, text: "สัตว์ทั้งป่ารู้ตัว นักล่าเลิกซุ่มแล้วเดินจากไป", effects: { safety: 15 } }] },
      {
        label: "เงียบแล้วค่อยๆ ถอย",
        outcomes: [
          { chance: 0.6, text: "คุณถอยออกมาได้อย่างเงียบเชียบ", effects: { safety: 5 } },
          { chance: 0.4, text: "เหยียบกิ่งไม้ดังกร๊อบ นักล่าพุ่งเข้ามา", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  }),
  sig("Rusa unicolor", "sambar_antler", {
    stageTags: ADULT,
    text: "เขาเก่าของคุณหลุดร่วงไป เขาใหม่กำลังงอกขึ้นมา หุ้มด้วยหนังนุ่มคล้ายกำมะหยี่",
    fact: "กวางป่าตัวผู้ผลัดเขาทุกปี เขาใหม่ที่กำลังงอกมีหนังหุ้มที่มีเส้นเลือดเลี้ยง เรียกว่าเขากำมะหยี่",
    choices: [
      { label: "กินให้มากเพื่อสร้างเขาใหม่", outcomes: [{ chance: 1, text: "เขาใหม่งอกสวยและแข็งแรงกว่าเดิม", effects: { fullness: 15, health: 10 } }] },
      { label: "หลบอยู่ในป่าทึบจนเขาแข็ง", outcomes: [{ chance: 1, text: "คุณระวังไม่ให้เขาอ่อนกระแทกอะไร", effects: { safety: 10, fullness: -5 } }] },
    ],
  }),
  sig("Equus quagga", "zebra_dazzle", {
    text: "สิงโตพุ่งออกมาจากพงหญ้า ฝูงม้าลายวิ่งแตกกระจายไปพร้อมกัน",
    fact: "ลายของม้าลายแต่ละตัวไม่ซ้ำกัน และงานวิจัยพบว่าลายช่วยให้แมลงดูดเลือดอย่างแมลงวันเกาะตัวยากขึ้น",
    choices: [
      { label: "วิ่งเกาะกลุ่มไปกับฝูง", outcomes: [{ chance: 1, text: "ลายของทั้งฝูงปนกันจนนักล่าเลือกเป้าไม่ถูก คุณรอด", effects: { fullness: -10, safety: 5 } }] },
      {
        label: "หันไปเตะสวนกลับ",
        outcomes: [
          { chance: 0.5, text: "กีบเท้าของคุณเตะโดนกรามสิงโต มันถอยไป", effects: { safety: 10 } },
          { chance: 0.5, text: "สิงโตอีกตัวตะปบหลังคุณ", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  }),
  sig("Alces alces", "moose_antlers", {
    stageTags: ADULT,
    text: "ฤดูผสมพันธุ์มาถึง กวางมูสตัวผู้อีกตัวเดินเข้ามาพร้อมเขากว้างใหญ่",
    fact: "กวางมูสตัวผู้มีเขากว้างได้เกือบ 2 เมตร ใช้ประลองกับคู่แข่งในฤดูผสมพันธุ์ แล้วผลัดทิ้งในฤดูหนาว",
    choices: [
      {
        label: "ประลองเขากัน",
        outcomes: [
          { chance: 0.55, text: "คุณผลักคู่แข่งถอยไปได้ ตัวเมียในแถบนี้เห็นหมด", effects: { safety: 5, addFlags: ["has_mate"] } },
          { chance: 0.45, text: "คุณแพ้และได้แผลที่คอ", effects: { health: -25, cause: "accident" } },
        ],
      },
      { label: "ถอยไปก่อน", outcomes: [{ chance: 1, text: "คุณเก็บแรงไว้สำหรับปีหน้า", effects: { fullness: 5 } }] },
    ],
  }),
  sig("Macaca fascicularis", "macaque_tools", {
    text: "น้ำลงแล้ว โขดหินริมทะเลเต็มไปด้วยหอยนางรม",
    fact: "ลิงแสมบางฝูงในไทยและเมียนมาใช้หินทุบเปลือกหอยและปูกินตามชายฝั่ง ลูกลิงเรียนวิธีใช้หินจากตัวที่โตกว่า",
    choices: [
      {
        label: "หยิบหินมาทุบเปลือกหอย",
        outcomes: [
          { chance: 0.75, text: "คุณทุบหอยนางรมแตกทีละตัว ได้กินจนอิ่ม", effects: { fullness: 30 } },
          { chance: 0.25, text: "หินพลาดไปโดนนิ้วตัวเอง", effects: { fullness: 10, health: -10 } },
        ],
      },
      { label: "ไปขโมยขนมจากนักท่องเที่ยว", outcomes: [{ chance: 1, text: "ได้ขนมมาถุงหนึ่ง แต่อาหารแบบนี้ไม่ดีกับสุขภาพ", effects: { fullness: 25, health: -5, safety: -5 } }] },
    ],
  }),
  sig("Manis javanica", "pangolin_ball", {
    text: "มีเสียงคนเดินถือไฟฉายเข้ามาในป่าตอนกลางคืน",
    fact: "ลิ่นม้วนตัวเป็นลูกกลมเมื่อถูกคุกคาม เกล็ดทำจากเคราตินชนิดเดียวกับเล็บคน และลิ่นเป็นสัตว์เลี้ยงลูกด้วยนมที่ถูกลักลอบค้ามากที่สุดในโลก",
    choices: [
      {
        label: "ม้วนตัวเป็นลูกกลม",
        outcomes: [
          { chance: 0.5, text: "เกล็ดแข็งกันเสือได้แต่กันคนไม่ได้ คุณถูกยัดใส่กระสอบ โชคดีที่กระสอบขาดจนคุณหนีออกมาได้", effects: { health: -15, safety: -10, cause: "human" } },
          { chance: 0.5, text: "แสงไฟส่องผ่านไปโดยไม่เห็นคุณ", effects: { safety: 5 } },
        ],
      },
      { label: "รีบมุดลงโพรงดิน", outcomes: [{ chance: 1, text: "คุณขุดดินด้วยเล็บแข็งแรงหายลงโพรงไปทันเวลา", effects: { fullness: -5, safety: 10 } }] },
    ],
  }),
  sig("Chaerephon plicatus", "bat_exodus", {
    text: "พระอาทิตย์ตกดิน ค้างคาวทั้งถ้ำเริ่มบินออกเป็นสายยาวเหมือนควันดำ",
    fact: "ค้างคาวปากย่นนับล้านตัวบินออกจากถ้ำเป็นสายยาวทุกเย็น และกินแมลงศัตรูพืชในนาข้าวรวมกันได้หลายตันต่อคืน",
    choices: [
      { label: "บินกลางสายไปกับฝูง", outcomes: [{ chance: 1, text: "เหยี่ยวที่รอปากถ้ำจับได้แค่ตัวที่บินแตกแถว คุณไปหากินในนาข้าวอย่างปลอดภัย", effects: { fullness: 25, safety: 5 } }] },
      {
        label: "บินออกก่อนใคร",
        outcomes: [
          { chance: 0.5, text: "คุณได้แมลงก่อนตัวอื่น", effects: { fullness: 30 } },
          { chance: 0.5, text: "เหยี่ยวที่รอปากถ้ำโฉบเข้าหาคุณ", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  }),
  sig("Rattus rattus", "rat_ship", {
    text: "คุณได้กลิ่นข้าวสารจากท่าเรือ เรือสินค้าลำใหญ่กำลังจะออก",
    fact: "หนูท้องขาวแพร่ไปทั่วโลกพร้อมกับเรือของมนุษย์ และกัดแทะได้ตลอดชีวิตเพราะฟันหน้างอกยาวขึ้นไม่หยุด",
    choices: [
      {
        label: "แอบขึ้นเรือ",
        outcomes: [
          { chance: 0.6, text: "ในเรือมีอาหารเต็มไปหมด คุณไปขึ้นฝั่งที่ท่าเรือแห่งใหม่", effects: { fullness: 30, move: "far" } },
          { chance: 0.4, text: "แมวประจำเรือรออยู่ คุณหนีลงน้ำทันเวลา", effects: { health: -20, cause: "predator" } },
        ],
      },
      { label: "อยู่ที่โกดังข้าวต่อ", outcomes: [{ chance: 1, text: "กระสอบข้าวในโกดังมีให้กินอีกนาน", effects: { fullness: 20 } }] },
    ],
  }),
  sig("Callosciurus finlaysonii", "squirrel_colors", {
    text: "คุณเห็นกระรอกหลากสีอีกตัวบนต้นไม้ข้างๆ แต่มันสีไม่เหมือนคุณเลย",
    fact: "กระรอกหลากสีมีสีขนต่างกันมากตามพื้นที่ บางถิ่นขาวทั้งตัว บางถิ่นแดง ดำ หรือมีหลายสีในตัวเดียว",
    choices: [
      { label: "ไล่ให้ออกไปจากต้นไม้ของคุณ", outcomes: [{ chance: 1, text: "มันวิ่งหนีไป ต้นไม้ที่ออกผลนี้ยังเป็นของคุณ", effects: { fullness: 15 } }] },
      { label: "ปล่อยให้กินด้วยกัน", outcomes: [{ chance: 1, text: "ผลไม้มีพอสำหรับสองตัว และมีตาคอยดูเหยี่ยวเพิ่มอีกคู่", effects: { fullness: 10, safety: 10 } }] },
    ],
  }),
  sig("Tupaia belangeri", "treeshrew", {
    text: "ชาวบ้านเดินผ่านมาแล้วชี้มาที่คุณ บอกลูกว่า 'ดูกระรอกตัวนั้นสิ'",
    fact: "กระแตไม่ใช่กระรอก แต่อยู่ในอันดับของตัวเอง (Scandentia) ซึ่งเป็นญาติใกล้ชิดกับไพรเมตอย่างลิงและคน",
    choices: [
      { label: "วิ่งหายเข้าพุ่มไม้", outcomes: [{ chance: 1, text: "คุณหลบไปหาหนอนกินในเศษใบไม้", effects: { fullness: 15 } }] },
      { label: "หยุดมองกลับไปเฉยๆ", outcomes: [{ chance: 1, text: "พวกเขาเดินจากไป คุณกลับไปหากินต่อ", effects: { fullness: 5, safety: 5 } }] },
    ],
  }),
  sig("Hydrochoerus hydrochaeris", "capybara_calm", {
    text: "นกหลายตัวมาเกาะบนหลังคุณ จิกกินเห็บอย่างสบายใจ",
    fact: "คาปิบาราเป็นสัตว์ฟันแทะที่ใหญ่ที่สุดในโลก ดำน้ำหลบนักล่าได้นานราว 5 นาที และสัตว์อื่นมักอยู่ใกล้ๆ อย่างไม่หวาดกลัว",
    choices: [
      { label: "นั่งนิ่งๆ ให้นกจิกเห็บ", outcomes: [{ chance: 1, text: "เห็บหายไปหมด คุณรู้สึกสบายตัว", effects: { health: 15 } }] },
      { label: "ลงไปแช่น้ำ", outcomes: [{ chance: 1, text: "น้ำเย็นสบาย และไกลจากเสือจากัวร์บนฝั่ง", effects: { safety: 10 } }] },
    ],
  }),
  sig("Macropus giganteus", "kangaroo_pouch", {
    stageTags: ["dependent"],
    text: "คุณโผล่หัวออกจากกระเป๋าหน้าท้องของแม่เป็นครั้งแรก โลกข้างนอกสว่างจ้า",
    fact: "จิงโจ้แรกเกิดตัวเล็กเท่าเม็ดถั่ว คลานเข้ากระเป๋าเองแล้วอยู่ในนั้นหลายเดือน แม่ยังหยุดการเจริญของตัวอ่อนตัวถัดไปไว้ชั่วคราวได้ระหว่างที่ยังมีลูกในกระเป๋า",
    choices: [
      { label: "ปีนออกไปสำรวจ", outcomes: [{ chance: 1, text: "คุณกระโดดได้ไม่กี่ก้าวก็รีบมุดกลับกระเป๋า", effects: { health: 5, safety: -5 } }] },
      { label: "อยู่ในกระเป๋าต่อ", outcomes: [{ chance: 1, text: "อบอุ่นและมีนมกินตลอดเวลา", effects: { fullness: 15, safety: 5 } }] },
    ],
  }),
  sig("Oryctolagus cuniculus", "rabbit_thump", {
    text: "คุณเห็นเงาเหยี่ยวบินวนอยู่เหนือทุ่งหญ้าที่เพื่อนๆ กำลังกินหญ้า",
    fact: "กระต่ายยุโรปขุดโพรงใต้ดินเชื่อมกันเป็นเครือข่ายใหญ่ และเตือนกันด้วยการกระทืบเท้าหลังลงพื้น",
    choices: [
      { label: "กระทืบเท้าเตือนแล้ววิ่งลงโพรง", outcomes: [{ chance: 1, text: "ทุกตัวหายลงโพรงทันเวลา", effects: { safety: 10, fullness: -5 } }] },
      {
        label: "กินหญ้าต่ออีกนิด",
        outcomes: [
          { chance: 0.6, text: "เหยี่ยวบินผ่านไป คุณได้กินหญ้าอ่อนเต็มที่", effects: { fullness: 20 } },
          { chance: 0.4, text: "เหยี่ยวโฉบลงมาเฉียดหลังคุณ", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  }),
  // ---------- สัตว์ทะเล ----------
  sig("Balaenoptera musculus", "blue_whale_krill", {
    stageTags: ADULT,
    text: "คุณเจอฝูงเคยหนาแน่นอยู่ลึกลงไปใต้ผิวน้ำ",
    fact: "วาฬสีน้ำเงินเป็นสัตว์ที่ใหญ่ที่สุดเท่าที่เคยมีมาบนโลก และกินเคยได้หลายตันต่อวันด้วยการอ้าปากฮุบน้ำแล้วกรองผ่านซี่กรอง",
    choices: [
      { label: "พุ่งฮุบเคยทั้งฝูง", outcomes: [{ chance: 1, text: "ปากของคุณขยายจนรับน้ำได้มหาศาล ได้เคยไปหลายร้อยกิโลกรัมในคำเดียว", effects: { fullness: 40 } }] },
      { label: "ว่ายหาฝูงที่ใหญ่กว่านี้", outcomes: [{ chance: 1, text: "คุณร้องเสียงทุ้มต่ำที่ดังไปได้ไกลหลายร้อยกิโลเมตร แล้วว่ายต่อไป", effects: { fullness: -10, move: "far" } }] },
    ],
  }),
  sig("Megaptera novaeangliae", "humpback_bubble_net", {
    stageTags: ADULT,
    text: "ฝูงปลาเฮร์ริงหนาแน่นอยู่ใต้ผิวน้ำ วาฬหลังค่อมตัวอื่นๆ ว่ายมารวมกลุ่มกับคุณ",
    fact: "วาฬหลังค่อมช่วยกันเป่าฟองอากาศเป็นวงล้อมฝูงปลาไว้เหมือนตาข่าย แล้วพุ่งขึ้นกลางวงพร้อมอ้าปากกว้าง",
    choices: [
      { label: "ร่วมกันเป่าตาข่ายฟองอากาศ", outcomes: [{ chance: 1, text: "ฝูงปลาถูกต้อนเป็นก้อน คุณพุ่งขึ้นกลางวงพร้อมเพื่อนๆ", effects: { fullness: 40 } }] },
      { label: "หากินตัวเดียว", outcomes: [{ chance: 1, text: "ได้ปลาบ้าง แต่ไม่มากเท่าตอนทำงานเป็นทีม", effects: { fullness: 15 } }] },
    ],
  }),
  sig("Orcinus orca", "orca_dialect", {
    text: "แม่ของคุณกำลังสอนวิธีล่าแมวน้ำบนชายหาด โดยไถตัวขึ้นไปบนหาดแล้วถอยกลับลงน้ำ",
    fact: "วาฬเพชฌฆาตแต่ละฝูงมีเสียงเรียกเฉพาะคล้ายภาษาถิ่น และลูกเรียนวิธีล่าแบบเฉพาะของฝูงจากแม่",
    choices: [
      {
        label: "ลองไถตัวขึ้นหาดตามแม่",
        outcomes: [
          { chance: 0.6, text: "คุณจับลูกแมวน้ำได้ตัวแรก แม่ช่วยดันคุณกลับลงน้ำ", effects: { fullness: 35 } },
          { chance: 0.4, text: "คุณติดอยู่บนหาดจนแม่ต้องมาช่วยดันกลับ", effects: { health: -15 } },
        ],
      },
      { label: "ดูแม่ทำก่อน", outcomes: [{ chance: 1, text: "คุณจำท่าทางไว้ แล้วกินแมวน้ำที่แม่แบ่งให้", effects: { fullness: 20, safety: 5 } }] },
    ],
  }),
  sig("Tursiops aduncus", "dolphin_sponge", {
    text: "พื้นทะเลตรงนี้เต็มไปด้วยเปลือกหอยและหินคม ปลาซ่อนอยู่ใต้ทราย",
    fact: "โลมาปากขวดอินโดแปซิฟิกบางตัวในอ่าวชาร์ก ออสเตรเลีย สวมฟองน้ำทะเลไว้ที่ปากเพื่อกันถลอกขณะคุ้ยหาปลาที่พื้นทะเล และส่งต่อวิธีนี้จากแม่สู่ลูก",
    choices: [
      { label: "ฉีกฟองน้ำทะเลมาสวมปาก", outcomes: [{ chance: 1, text: "คุณคุ้ยทรายหาปลาได้โดยไม่ถลอกเลย", effects: { fullness: 30 } }] },
      { label: "ใช้เสียงโซนาร์หาปลาในน้ำเปิด", outcomes: [{ chance: 1, text: "เสียงสะท้อนบอกตำแหน่งฝูงปลา คุณไล่จับได้หลายตัว", effects: { fullness: 20 } }] },
    ],
  }),
  sig("Pusa hispida", "ringed_seal_lair", {
    stageTags: ADULT,
    text: "ใกล้ถึงเวลาคลอดลูก หิมะทับถมหนาบนแผ่นน้ำแข็ง",
    fact: "แมวน้ำวงแหวนขุดถ้ำในกองหิมะบนแผ่นน้ำแข็งเพื่อคลอดและซ่อนลูกจากหมีขั้วโลก โดยมีรูลงน้ำอยู่ในถ้ำ",
    choices: [
      { label: "ขุดถ้ำหิมะที่มีรูลงน้ำ", outcomes: [{ chance: 1, text: "ถ้ำหิมะอบอุ่นและมีทางหนีลงน้ำ", effects: { safety: 15, fullness: -10 } }] },
      {
        label: "นอนบนน้ำแข็งเปิดโล่ง",
        outcomes: [
          { chance: 0.5, text: "วันนี้สงบดี", effects: { health: 5 } },
          { chance: 0.5, text: "หมีขั้วโลกเห็นคุณจากที่ไกล คุณลงน้ำได้ทันแบบหวุดหวิด", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  }),
  sig("Hydrurga leptonyx", "leopard_seal_penguins", {
    stageTags: ADULT,
    text: "เพนกวินกลุ่มใหญ่ยืนลังเลอยู่ที่ขอบน้ำแข็ง ไม่มีตัวไหนกล้าลงน้ำก่อน",
    fact: "แมวน้ำเสือดาวเป็นนักล่ารายใหญ่ของเพนกวิน แต่ก็กินเคยด้วยการกรองน้ำผ่านฟันกรามที่มีแฉกหลายซี่",
    choices: [
      {
        label: "ซุ่มรอใต้ขอบน้ำแข็ง",
        outcomes: [
          { chance: 0.6, text: "เพนกวินตัวแรกกระโดดลงมา คุณงับได้ทันที", effects: { fullness: 35 } },
          { chance: 0.4, text: "เพนกวินลงน้ำพร้อมกันทั้งกลุ่มจนคุณจับไม่ทัน", effects: { fullness: -5 } },
        ],
      },
      { label: "กรองกินเคยแทน", outcomes: [{ chance: 1, text: "เคยมีมากมาย ไม่ต้องไล่ใคร", effects: { fullness: 20 } }] },
    ],
  }),
];
